import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { OrderCard } from "../../components";
import AppPopup from "../../components/AppPopup/AppPopup";
import "./styles.css";
import { useRootStore } from "../../stores";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { OrderType } from "../../interfaces_and_types";

type QueueTab = "waiting" | "preparing" | "ready";

const TIME_OPTIONS = [
  { label: "5", value: "00:05:00" },
  { label: "10", value: "00:10:00" },
  { label: "15", value: "00:15:00" },
  { label: "20", value: "00:20:00" },
  { label: "25", value: "00:25:00" },
  { label: "30", value: "00:30:00" },
  { label: "35", value: "00:35:00" },
  { label: "40", value: "00:40:00" },
];

const Main = () => {
  const orderStore = useRootStore().rootStore.orderStore;
  const {
    ordersFetched,
    fetchOrders,
    changeOrdersFetched,
    fetchOrdersStatus,
    ordersStatusFetched,
    changeOrdersStatusFetched,
    waitingOrders,
    preparingOrders,
    readyOrders,
    canDropToQueue,
    dragToPreparing,
    dragToReady,
  } = orderStore;
  const { fetchLoading, changeFetchLoading, token } =
    useRootStore().rootStore.placeStore;
  const { setCurrentPage } = useRootStore().rootStore.drawerStore;
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<QueueTab>("waiting");
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 650px)").matches,
  );
  const [dragOverQueue, setDragOverQueue] = useState<QueueTab | null>(null);
  const [draggedOrderId, setDraggedOrderId] = useState<number | null>(null);

  const [timePopupOpen, setTimePopupOpen] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState<number | null>(null);

  const getOrders = async () => {
    if (!ordersFetched) {
      changeFetchLoading(true);
      changeOrdersFetched(true);
      setCurrentPage("main");
      !ordersFetched && token !== null && (await fetchOrders(token));
      changeFetchLoading(false);
      changeOrdersFetched(true);
    }
  };

  const getOrdersStatus = async () => {
    if (!ordersStatusFetched) {
      changeOrdersStatusFetched(true);
      !ordersStatusFetched &&
        token !== null &&
        (await fetchOrdersStatus(token));
    }
  };

  useEffect(() => {
    getOrders();
    getOrdersStatus();
    const mql = window.matchMedia("(max-width: 650px)");
    const handler = (ev: MediaQueryListEvent) => setIsMobile(ev.matches);
    mql.addEventListener("change", handler);

    // Prevent page-level vertical scroll while kanban is mounted
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      mql.removeEventListener("change", handler);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findOrderById = useCallback(
    (id: number): OrderType | undefined => {
      return (
        waitingOrders.find((o) => o.id === id) ??
        preparingOrders.find((o) => o.id === id) ??
        readyOrders.find((o) => o.id === id)
      );
    },
    [waitingOrders, preparingOrders, readyOrders],
  );

  const handleDragStart = (e: React.DragEvent, orderId: number) => {
    e.dataTransfer.setData("text/plain", String(orderId));
    e.dataTransfer.effectAllowed = "move";
    setDraggedOrderId(orderId);
  };

  const handleDragOver = (e: React.DragEvent, queue: QueueTab) => {
    e.preventDefault();
    if (queue === "waiting") return;
    if (draggedOrderId !== null) {
      const order = findOrderById(draggedOrderId);
      if (order && canDropToQueue(order, queue as "preparing" | "ready")) {
        e.dataTransfer.dropEffect = "move";
        setDragOverQueue(queue);
        return;
      }
    }
    e.dataTransfer.dropEffect = "none";
  };

  const handleDragLeave = () => {
    setDragOverQueue(null);
  };

  const handleDrop = async (e: React.DragEvent, targetQueue: QueueTab) => {
    e.preventDefault();
    setDragOverQueue(null);
    setDraggedOrderId(null);

    if (targetQueue === "waiting") return;

    const orderId = Number(e.dataTransfer.getData("text/plain"));
    const order = findOrderById(orderId);
    if (!order || token === null) return;

    if (targetQueue === "preparing" && canDropToQueue(order, "preparing")) {
      setPendingOrderId(orderId);
      setTimePopupOpen(true);
    } else if (targetQueue === "ready" && canDropToQueue(order, "ready")) {
      await dragToReady(orderId, token);
    }
  };

  const handleTimeSelect = async (estimatedTime: string) => {
    setTimePopupOpen(false);
    if (pendingOrderId !== null && token !== null) {
      await dragToPreparing(pendingOrderId, token, estimatedTime);
    }
    setPendingOrderId(null);
  };

  const handleTimeCancel = () => {
    setTimePopupOpen(false);
    setPendingOrderId(null);
  };

  const handleDragEnd = () => {
    setDragOverQueue(null);
    setDraggedOrderId(null);
  };

  const tabs: { key: QueueTab; label: string; count: number }[] = [
    { key: "waiting", label: t("New orders"), count: waitingOrders.length },
    { key: "preparing", label: t("Preparing"), count: preparingOrders.length },
    { key: "ready", label: t("Ready"), count: readyOrders.length },
  ];

  const renderColumn = (
    tab: QueueTab,
    content: React.ReactNode,
    count: number,
    emptyText: string,
  ) => (
    <div
      className={`kanban-column ${isMobile && activeTab !== tab ? "kanban-column--hidden" : ""} ${dragOverQueue === tab ? "kanban-column--drag-over" : ""}`}
      onDragOver={(e) => handleDragOver(e, tab)}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop(e, tab)}
    >
      <div className="kanban-column-body">
        {count > 0 ? content : <p className="kanban-empty">{emptyText}</p>}
      </div>
    </div>
  );

  if (!fetchLoading)
    return (
      <div className="main">
        <AppPopup
          open={timePopupOpen}
          setOpen={setTimePopupOpen}
          title={t("Time to prepare")}
          xButton
          xButtonOnClick={handleTimeCancel}
          choices={TIME_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className="popup_modal_choice"
              onClick={() => handleTimeSelect(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        />
        {isMobile && (
          <div className="kanban-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`kanban-tab kanban-tab--${tab.key} ${activeTab === tab.key ? "kanban-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span>{tab.label}</span>
                <span className="kanban-tab-badge">{tab.count}</span>
              </button>
            ))}
          </div>
        )}
        <div className="kanban-header-bar">
          <div className="kanban-header-cell kanban-header-cell--waiting">
            <span>{t("New orders")}</span>
            <span className="kanban-count-badge">{waitingOrders.length}</span>
          </div>
          <div className="kanban-header-cell kanban-header-cell--preparing">
            <span>{t("Preparing")}</span>
            <span className="kanban-count-badge">{preparingOrders.length}</span>
          </div>
          <div className="kanban-header-cell kanban-header-cell--ready">
            <span>{t("Ready")}</span>
            <span className="kanban-count-badge">{readyOrders.length}</span>
          </div>
        </div>
        <div className="kanban-board">
          {renderColumn(
            "waiting",
            waitingOrders.map((order) => (
              <div
                key={order.id}
                draggable
                onDragStart={(e) => handleDragStart(e, order.id)}
                onDragEnd={handleDragEnd}
                className="kanban-draggable"
              >
                <OrderCard order={order} />
              </div>
            )),
            waitingOrders.length,
            t("No new orders"),
          )}
          {renderColumn(
            "preparing",
            preparingOrders.map((order) => (
              <div
                key={order.id}
                draggable
                onDragStart={(e) => handleDragStart(e, order.id)}
                onDragEnd={handleDragEnd}
                className="kanban-draggable"
              >
                <OrderCard order={order} />
              </div>
            )),
            preparingOrders.length,
            t("No orders preparing"),
          )}
          {renderColumn(
            "ready",
            readyOrders.map((order) => (
              <div
                key={order.id}
                draggable
                onDragStart={(e) => handleDragStart(e, order.id)}
                onDragEnd={handleDragEnd}
                className="kanban-draggable"
              >
                <OrderCard order={order} />
              </div>
            )),
            readyOrders.length,
            t("No orders ready"),
          )}
        </div>
      </div>
    );
  else
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100vw",
          height: "100vh",
        }}
      >
        <CircularProgress
          className="MuiCircularProgress-colorPrimary"
          size={50}
        />
      </div>
    );
};

export default observer(Main);
